import React, { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import {
    Home, Users, FileText, Mail, Calendar, BookOpen, Shield, Settings,
    CreditCard, Scale, Wand2, Workflow, Lock, Gavel,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

/* ================================================================
   Nexus One GlobalNav — Sprint I-3 (Palantir Gotham feel)
   D2: Orchestrations added — Phase 5-B active.
   Sprint K: Legal Analysis added under Legal group.
   ================================================================ */

const NAV_ITEMS = [
    { to: "/app",                   icon: Home,       label: "Home",            testid: "nav-home" },
    { to: "/app/workforce",         icon: Users,      label: "Workforce",       testid: "nav-workforce" },
    { to: "/app/skills",            icon: Wand2,      label: "Skill Builder",   testid: "nav-skill-builder" },
    { to: "/app/orchestrations",    icon: Workflow,   label: "Orchestrations",  testid: "nav-orchestrations" },
    { to: "/app/legal",             icon: Scale,      label: "Legal",           testid: "nav-legal" },
    { to: "/app/legal/analysis",    icon: Gavel,      label: "Legal Analysis",  testid: "nav-legal-analysis" },
    { to: "/app/documents",         icon: FileText,   label: "Documents",       testid: "nav-documents" },
    { to: "/app/email",             icon: Mail,       label: "Email",           testid: "nav-email" },
    { to: "/app/meetings",          icon: Calendar,   label: "Meetings",        testid: "nav-meetings" },
    { to: "/app/knowledge",         icon: BookOpen,   label: "Knowledge",       testid: "nav-knowledge" },
    { to: "/app/billing",           icon: CreditCard, label: "Billing",         testid: "nav-billing" },
    { to: "/app/settings",          icon: Settings,   label: "Settings",        testid: "nav-settings" },
];

export default function GlobalNav() {
    const { user } = useAuth();
    const isOwner = user?.role === "platform_owner";
    // Sprint J-3-G · resolve authoring gate so we can lock the Skill Builder icon.
    const [skillLocked, setSkillLocked] = useState(false);
    useEffect(() => {
        if (isOwner) { setSkillLocked(false); return; }
        api.get("/access/summary").then(({ data }) => {
            if (data.owner) return setSkillLocked(false);
            setSkillLocked(data?.usage?.skill_author?.cap === 0);
        }).catch(() => setSkillLocked(false));
        const onChange = () => {
            api.get("/access/summary").then(({ data }) => {
                setSkillLocked(!data.owner && data?.usage?.skill_author?.cap === 0);
            }).catch(() => {});
        };
        window.addEventListener("wfos:access-changed", onChange);
        return () => window.removeEventListener("wfos:access-changed", onChange);
    }, [isOwner]);

    return (
        <nav
            className="w-[72px] flex-shrink-0 flex flex-col items-center py-4 gap-2 overflow-y-auto atlas-scrollbar relative"
            style={{
                background: "var(--atlas-gradient-nav)",
                borderRight: "1px solid rgba(139, 92, 246, 0.14)",
                boxShadow: "inset -1px 0 0 rgba(255,255,255,0.02)",
            }}
        >
            <Link
                to="/app"
                data-testid="nav-logo"
                className="atlas-logo-mark w-10 h-10 text-[13px]"
                title="Nexus One"
            >
                A
            </Link>
            <div className="w-8 h-px my-2" style={{ background: "rgba(139,92,246,0.14)" }} />

            {NAV_ITEMS.map((it) => {
                const Icon = it.icon;
                const isSkillBuilder = it.to === "/app/skills";
                const showLock = isSkillBuilder && skillLocked;
                return (
                    <NavLink
                        key={it.to}
                        to={it.to}
                        end={it.to === "/app"}
                        title={showLock ? "Skill authoring requires a paid plan. Upgrade to build." : it.label}
                        data-testid={showLock ? "nav-skillbuilder-locked" : it.testid}
                        className={({ isActive }) =>
                            [
                                "group relative w-11 h-11 rounded-lg grid place-items-center transition-all duration-150",
                                isActive
                                    ? "atlas-nav-active"
                                    : "atlas-nav-idle",
                            ].join(" ")
                        }
                        style={({ isActive }) =>
                            isActive
                                ? {
                                      background: "linear-gradient(90deg, rgba(139,92,246,0.22) 0%, rgba(139,92,246,0.06) 100%)",
                                      color: "var(--atlas-primary-hover)",
                                      boxShadow: "inset 2px 0 0 var(--atlas-primary), 0 0 20px -6px var(--atlas-primary-glow)",
                                  }
                                : {
                                      color: "var(--atlas-text-muted)",
                                  }
                        }
                    >
                        <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                        {showLock && (
                            <span
                                className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full grid place-items-center"
                                style={{
                                    background: "var(--atlas-bg-deep)",
                                    border: "1px solid var(--atlas-border-strong)",
                                }}
                                aria-hidden="true"
                            >
                                <Lock className="w-2 h-2" strokeWidth={2.5} style={{ color: "var(--atlas-text-tertiary)" }} />
                            </span>
                        )}
                        {/* Tooltip on hover */}
                        <span
                            className="pointer-events-none absolute left-full ml-3 whitespace-nowrap rounded-md px-2 py-1 text-[11px] font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{
                                background: "var(--atlas-bg-elevated)",
                                border: "1px solid var(--atlas-border-strong)",
                                color: "var(--atlas-text-primary)",
                                boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                            }}
                        >
                            {showLock ? "Skill authoring · upgrade to build" : it.label}
                        </span>
                    </NavLink>
                );
            })}

            {isOwner && (
                <>
                    <div className="w-8 h-px my-2" style={{ background: "rgba(245,158,11,0.20)" }} />
                    <NavLink
                        to="/app/platform"
                        title="Platform Owner Console"
                        data-testid="nav-platform"
                        className="group relative w-11 h-11 rounded-lg grid place-items-center transition-all duration-150"
                        style={({ isActive }) =>
                            isActive
                                ? {
                                      background: "linear-gradient(90deg, rgba(245,158,11,0.22) 0%, rgba(245,158,11,0.06) 100%)",
                                      color: "#fbbf24",
                                      boxShadow: "inset 2px 0 0 #f59e0b, 0 0 20px -6px rgba(245,158,11,0.5)",
                                  }
                                : { color: "#f59e0b" }
                        }
                    >
                        <Shield className="w-[18px] h-[18px]" strokeWidth={1.75} />
                        <span
                            className="pointer-events-none absolute left-full ml-3 whitespace-nowrap rounded-md px-2 py-1 text-[11px] font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{
                                background: "var(--atlas-bg-elevated)",
                                border: "1px solid rgba(245,158,11,0.35)",
                                color: "#fbbf24",
                                boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                            }}
                        >
                            Owner Console
                        </span>
                    </NavLink>
                </>
            )}
        </nav>
    );
}
